import { DeleteAccountDialog, ProfileSettings } from "@gladis/domain-identity/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Page } from "../../../components/page.js";
import { useAuth } from "../../../lib/auth-context.js";
import { useT } from "@gladis/ui/i18n";

export const Route = createFileRoute("/_authenticated/_shell/settings_/profile")({
  component: ProfileSettingsPage,
});

/**
 * Profile settings — you, rather than the household you are in.
 *
 * The other half of what `/settings` used to be; `settings_.household.tsx` carries the argument for the
 * split. The short version is that this page does not change when you switch households and the other one
 * does, which the old page's own comment already said.
 *
 * IT NEEDS NO `me` QUERY. The old page read `/me` because `HouseholdSettings` takes it as a prop;
 * `ProfileSettings` fetches its own, so splitting the page removed a prop rather than duplicating a read.
 *
 * `DeleteAccountDialog` belongs here and not on the household page, for the same reason the split exists:
 * deleting your account is about you. It stays at the foot of the page, below the ordinary controls,
 * because it is the one thing here that cannot be undone.
 */
function ProfileSettingsPage(): ReactNode {
  const t = useT();
  const { clear } = useAuth();

  return (
    <Page>
      <Page.Title>{t("nav.profileSettings")}</Page.Title>
      <Page.Body>
        {/* Forms keep the narrow reading measure even though the column is wide — a text input
            stretched across 1400px is unusable. */}
        <div className="col-span-full max-w-content">
          <ProfileSettings />

          <DeleteAccountDialog
            onDeleted={() => {
              // The session is already dead server-side; clearing locally avoids one doomed
              // request before the redirect.
              clear();
              window.location.assign("/sign-in");
            }}
          />
        </div>
      </Page.Body>
    </Page>
  );
}
